import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ComponentProps,
  type ReactNode,
} from 'react'
import { Button } from '../primitives/Button'
import { Overlay } from '../overlay/Overlay'
import { CategoryIcon } from './CategoryIcon'
import { useBreakpoint } from '../responsive/useBreakpoint'
import { useHitTarget } from '../responsive/hitTarget'
import { IconCataloguePicker } from './IconCataloguePicker'
import type { CatalogueIcon } from './useIconCatalogue'
import './iconCatalogue.css'

/**
 * The icon picker as a PANE of a dialog that already exists — #455, lifted out
 * of `CategoryForm.tsx` by #461 so the merchant form gets the same thing
 * rather than a second implementation of it.
 *
 * **Never a second `Overlay`.** The pane is a region inside the caller's own
 * modal: one focus trap, one inert sweep, one Escape owner. A stacked second
 * modal is where #215, #355 and #362 came from. What this adds on top of that
 * one Escape is a layer — while the pane is open the key closes the PANE and
 * stops there (`onLayoutKeyDown`), so the dialog closes on the next press.
 *
 * **Two layouts, chosen by `useBreakpoint()` and nothing else** (M0-R5;
 * `single-source.test.ts` forbids the media query that would otherwise say
 * it). At ≥ 640px `IconPaneDialog` widens its `Overlay` (`pane.overlaySize`)
 * and the pane sits in a second column beside the form. On a phone it takes
 * the form's place in the same sheet, with Back.
 *
 * **On a phone the form is UNMOUNTED, not hidden — `IconPaneDialog` does it.**
 * `useFocusTrap` counts focusable descendants without regard to `inert` or
 * `visibility`, so a form merely hidden behind the pane would still be the
 * trap's first stop and a Shift+Tab from Back would walk out of the dialog.
 * `pane.formVisible` says when to render it; the form's state lives in the
 * caller, so nothing typed is lost.
 */

/** What `useIconPane` hands its caller, and what the components below take. */
export type IconPaneState = {
  /** Whether the pane is open. */
  open: boolean
  /** Whether the form is rendered — false only on a phone with the pane
   *  open. `IconPaneDialog` reads it; see "unmount, not hide" above. */
  formVisible: boolean
  /** The `size` of the dialog's `Overlay` — `'wide'` only on desktop with
   *  the pane open, `'default'` otherwise (including every phone state, where
   *  the pane replaces the form in the same sheet rather than sitting beside
   *  it). #461: the merchant form built this condition inline and, being a
   *  caller-side obligation nothing enforced, never passed it to its own
   *  `Overlay` — group labels truncated and the grid ran four columns wide.
   *  Computed here and applied by `IconPaneDialog`, so no caller holds it. */
  overlaySize: 'default' | 'wide'
  /** The icon picked IN THIS SESSION, or null — never a stored one. That
   *  distinction is the wire rule both callers implement: `icon` is sent only
   *  when this is set, because a stored value may be a legacy slug the
   *  catalogue does not know and resending it would 422 a save that never
   *  touched the icon (#445/#446/#461). */
  picked: CatalogueIcon | null
  /** The slug to show as selected in the grid — the pick, else whatever the
   *  record being edited already carries. Set by the caller through
   *  `useIconPane`'s `storedIcon`. Showing it checked sends nothing. */
  selected: string | null
  /** Record a pick. On a phone this also closes the pane: there is no room to
   *  show the pick beside the picker, so a pick IS the way back to the form.
   *  Desktop stays open, with the pick visible in the form beside it. */
  pick: (icon: CatalogueIcon) => void
  /** Open, close, or toggle — `toggle` is what a single "Choose icon" button
   *  wants, since one control names both states and `aria-expanded` says
   *  which. */
  openPane: () => void
  closePane: () => void
  toggle: () => void
  /** Goes on the pane layout element — see `IconPaneLayout`. */
  onLayoutKeyDown: (event: KeyboardEvent) => void
  /** `id` of the pane, for the opener's `aria-controls`. */
  paneId: string
  /** Ref for the opener, so focus returns to it when the pane closes. */
  chooseRef: React.RefObject<HTMLButtonElement | null>
  /** Internal: the pane element, for the focus effect. */
  paneRef: React.RefObject<HTMLElement | null>
  /** Internal: which layout is in force. */
  phone: boolean
}

/**
 * The pane's state, focus behaviour and Escape layering.
 *
 * Closed by default: the catalogue is not fetched until the user asks for it,
 * so a form that never touches the icon costs no icon requests.
 */
export function useIconPane({ storedIcon }: { storedIcon?: string | null } = {}): IconPaneState {
  const [open, setOpen] = useState(false)
  const [picked, setPicked] = useState<CatalogueIcon | null>(null)
  // Called unconditionally — `Overlay`'s own note on why a hook never sits
  // behind `&&` applies here too.
  const phone = useBreakpoint() === 'phone'
  const paneId = useId()
  const paneRef = useRef<HTMLElement>(null)
  const chooseRef = useRef<HTMLButtonElement>(null)
  // Set by `closePane` alone, so focus returns to the opener on a CLOSE —
  // never on first render, when nothing was opened and focus is the dialog's
  // to place.
  const returnFocus = useRef(false)

  const closePane = useCallback(() => {
    returnFocus.current = true
    setOpen(false)
  }, [])

  const openPane = useCallback(() => setOpen(true), [])

  // Focus follows the pane — into its search field when it opens, back to the
  // control that opened it when it closes. In an effect rather than in the
  // handlers because both targets may not exist yet at click time: the pane is
  // not mounted until this render commits, and on a phone neither is the form
  // the opener lives in. The search is found by role rather than by a ref
  // because `IconCataloguePicker` is reused unchanged and exposes none.
  useEffect(() => {
    if (open) {
      paneRef.current?.querySelector<HTMLInputElement>('input[type="search"]')?.focus()
      return
    }
    if (!returnFocus.current) return
    returnFocus.current = false
    chooseRef.current?.focus()
  }, [open])

  return {
    open,
    formVisible: !(phone && open),
    overlaySize: open && !phone ? 'wide' : 'default',
    picked,
    selected: picked?.slug ?? storedIcon ?? null,
    pick: (icon: CatalogueIcon) => {
      setPicked(icon)
      if (phone) closePane()
    },
    openPane,
    closePane,
    toggle: () => (open ? closePane() : openPane()),
    /**
     * Escape, layered over the dialog's. The panel's own handler closes the
     * dialog on ANY Escape that reaches it; this sits inside the panel, so a
     * key from anywhere in the form or the pane passes here first, and
     * `stopPropagation` keeps it from reaching the panel while the pane is
     * open. React's synthetic `stopPropagation` is what makes that work —
     * both handlers are React props on ancestors of the focused node, so the
     * inner one stopping the event means the outer one never runs. With the
     * pane closed this does nothing and the dialog closes as before.
     */
    onLayoutKeyDown: (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !open) return
      event.stopPropagation()
      closePane()
    },
    paneId,
    chooseRef,
    paneRef,
    phone,
    // `setPicked` itself is not exposed: a pick has to go through `pick` so
    // the phone's close-on-pick happens, and a caller setting it directly
    // would be a second way for the wire rule above to be got wrong.
  }
}

/**
 * The dialog's content box, which the pane opens into — the element that owns
 * the split/overlay layout and the layered Escape.
 *
 * A keyboard handler on a non-interactive element, deliberately: it owns no
 * key of its own, it only intercepts Escape bubbling up from the controls
 * inside it.
 */
function IconPaneLayout({ pane, children }: { pane: IconPaneState; children: ReactNode }) {
  const modifier = pane.open ? (pane.phone ? 'icon-pane-layout--over' : 'icon-pane-layout--split') : null
  return (
    <div
      className={['icon-pane-layout', modifier].filter(Boolean).join(' ')}
      onKeyDown={pane.onLayoutKeyDown}
    >
      {children}
    </div>
  )
}

/**
 * The pane itself. Renders nothing until it is opened.
 *
 * Outside the caller's `<form>`, so nothing in it can submit — which is why
 * this is a sibling of the form inside `IconPaneLayout` rather than a child
 * of it. A labelled `<section>` — a region named "Choose icon" — so a
 * screen-reader user landing in the search field knows where they are.
 *
 * `slide-in` is `motion.css`'s (M0-R20, the user's decision of 2026-09-22):
 * the pane arrives from the right — beside the form on desktop, over the
 * form's place on a phone — and reduced motion switches it off. The layout
 * clips the offset. Closing is instant: see M0-R20 for why there is no
 * slide-out.
 */
function IconPane({ pane }: { pane: IconPaneState }) {
  const titleId = useId()
  // `'form'` (33px), matching the `Button size="form"` controls beside it;
  // below 640px every size class alike returns the coarse 44px floor.
  const target = useHitTarget('form')
  if (!pane.open) return null

  return (
    <section
      ref={pane.paneRef}
      id={pane.paneId}
      className="icon-pane slide-in"
      aria-labelledby={titleId}
    >
      <div className="icon-pane__head">
        {pane.phone && (
          <Button type="button" variant="chip" size="form" onClick={pane.closePane}>
            Back
          </Button>
        )}
        <h3 id={titleId} className="icon-pane__title">
          Choose icon
        </h3>
        {!pane.phone && (
          <button
            type="button"
            className="icon-pane__close hoverable"
            style={{ minHeight: target, minWidth: target }}
            onClick={pane.closePane}
            aria-label="Close icon picker"
          >
            <span aria-hidden="true">×</span>
          </button>
        )}
      </div>
      <IconCataloguePicker
        value={pane.selected}
        onPick={(icon) => {
          pane.pick(icon)
        }}
      />
    </section>
  )
}

/**
 * The "Choose icon" button and the pick beside it — the row both forms open
 * the pane from, driven entirely by the pane's own values (#467; it was
 * duplicated verbatim in `CategoryForm` and `MerchantForm`). The caller
 * supplies the field group and its visible label around it.
 *
 * One name in both states; `aria-expanded` says which. The glyph beside the
 * pick is decorative — the label next to it names the pick.
 */
export function IconPaneOpener({ pane }: { pane: IconPaneState }) {
  const { picked } = pane
  return (
    <div className="icon-pane-opener">
      <Button
        ref={pane.chooseRef}
        type="button"
        variant="chip"
        size="form"
        aria-expanded={pane.open}
        aria-controls={pane.open ? pane.paneId : undefined}
        onClick={pane.toggle}
      >
        Choose icon
      </Button>
      {picked !== null && (
        <span className="icon-pane-opener__picked">
          <span className="icon-pane-opener__glyph" aria-hidden="true">
            <CategoryIcon
              category={{ name: picked.label, icon: picked.slug, icon_url: picked.icon_url }}
              size={16}
            />
          </span>
          <span className="icon-pane-opener__label">{picked.label}</span>
        </span>
      )}
    </div>
  )
}

/**
 * The dialog that hosts a form and its icon pane — the one component a caller
 * mounts, so the three things `useIconPane` asks of it are not the caller's
 * to remember (#467; they were a docblock until then):
 *
 * - the `Overlay`'s `size` is `pane.overlaySize` (#461 — a form that forgot it
 *   shipped a 440px pane);
 * - `children` — the form — are rendered only while `pane.formVisible`, i.e.
 *   UNMOUNTED on a phone with the pane open (see "unmount, not hide" above);
 * - the whole is wrapped in `IconPaneLayout` and the pane rendered beside it.
 *
 * `size` and `open` are not accepted: the first is the pane's to decide, the
 * second is always true — callers mount this only while their dialog is shown.
 */
export function IconPaneDialog({
  pane,
  children,
  ...overlay
}: { pane: IconPaneState; children: ReactNode } & Omit<
  ComponentProps<typeof Overlay>,
  'size' | 'open' | 'children'
>) {
  return (
    <Overlay {...overlay} open size={pane.overlaySize}>
      <IconPaneLayout pane={pane}>
        {pane.formVisible && children}
        <IconPane pane={pane} />
      </IconPaneLayout>
    </Overlay>
  )
}
